import { getModernSignatureIconSize, getModernSignatureIconSrc, } from "../signatureIconAssets";
const BRAND_RED = "#cc0000";
const TEXT_DARK = "#111111";
const TEXT_BODY = "#333333";
const FONT_STACK = "'Segoe UI', Arial, Helvetica, sans-serif";
const DEFAULT_COMPANY_NAME = "KRASH CONSULTING";
const DEFAULT_WEBSITE_URL = "https://www.krashconsulting.com/";
const DEFAULT_LOCATION = "Hyderabad, Telangana, India";
/**
 * Modern signature template.
 *
 * Layout based on htmlsignature.html:
 * - top accent bar
 * - circular photo and social icons on the left
 * - name, title, company, and contact details on the right
 */
export function buildModernTemplate(profile, prefs, iconMode = "dataUri") {
    const p = mergeWithOverrides(profile, prefs);
    const companyName = p.companyName || DEFAULT_COMPANY_NAME;
    const primaryPhone = p.mobilePhone || p.directPhone;
    const websiteUrl = prefs.websiteUrl || DEFAULT_WEBSITE_URL;
    const websiteLabel = formatWebsiteLabel(websiteUrl);
    const socialProfile = resolveSocialProfile(p, prefs);
    const locationText = formatLocation(p, {
        omitState: socialProfile.usingStateFallback,
        omitStreetAddress: socialProfile.usingStreetFallback,
    });
    const socialLinks = [
        socialProfile.twitterHandle
            ? buildSocialLink(`https://x.com/${socialProfile.twitterHandle}`, getModernSignatureIconSrc("x", iconMode), "X", "x")
            : "",
        socialProfile.linkedInUrl
            ? buildSocialLink(socialProfile.linkedInUrl, getModernSignatureIconSrc("linkedin", iconMode), "LinkedIn", "linkedin")
            : "",
    ]
        .filter(Boolean)
        .join("");
    const detailRows = [
        primaryPhone
            ? buildDetailRow(getModernSignatureIconSrc("phone", iconMode), `<a href="tel:${escapeAttr(primaryPhone)}" style="color:${TEXT_BODY}; text-decoration:none; font-size:13px; font-family:${FONT_STACK};">${escapeHtml(primaryPhone)}</a>`, "phone")
            : "",
        p.email
            ? buildDetailRow(getModernSignatureIconSrc("email", iconMode), `<a href="mailto:${escapeAttr(p.email)}" style="color:${TEXT_BODY}; text-decoration:none; font-size:13px; font-family:${FONT_STACK};">${escapeHtml(p.email)}</a>`, "email")
            : "",
        websiteUrl
            ? buildDetailRow(getModernSignatureIconSrc("website", iconMode), `<a href="${escapeAttr(websiteUrl)}" style="color:${BRAND_RED}; text-decoration:none; font-size:13px; font-weight:600; font-family:${FONT_STACK};">${escapeHtml(websiteLabel)}</a>`, "website")
            : "",
        locationText
            ? buildDetailRow(getModernSignatureIconSrc("location", iconMode), escapeHtml(locationText), "location", true)
            : "",
    ]
        .filter(Boolean)
        .join("");
    return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:${FONT_STACK}; max-width:560px; background-color:#ffffff;">
  <tr>
    <td style="padding:0 0 12px 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td style="height:3px; background-color:${BRAND_RED}; font-size:1px; line-height:1px;">&nbsp;</td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td>
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="vertical-align:top; padding-right:18px;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="text-align:center;">
                  <img
                    src="${p.photoDataUri}"
                    width="100"
                    height="100"
                    alt="${escapeHtml(p.displayName)}"
                    border="0"
                    style="display:block; width:100px; height:100px; border-radius:50%; object-fit:cover;"
                  />
                </td>
              </tr>
              ${socialLinks
        ? `<tr>
                <td style="padding-top:10px; text-align:center;">
                  ${socialLinks}
                </td>
              </tr>`
        : ""}
            </table>
          </td>
          <td style="vertical-align:top;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="font-size:20px; font-weight:700; color:${TEXT_DARK}; letter-spacing:0.5px; padding-bottom:2px; line-height:1.2;">
                  ${escapeHtml(p.displayName)}
                </td>
              </tr>
              ${p.jobTitle
        ? `<tr>
                <td style="font-size:11px; font-weight:600; color:${BRAND_RED}; text-transform:uppercase; letter-spacing:1.2px; padding-bottom:4px;">
                  ${escapeHtml(p.jobTitle)}
                </td>
              </tr>`
        : ""}
              <tr>
                <td style="font-size:12px; font-weight:700; color:${TEXT_DARK}; letter-spacing:2px; text-transform:uppercase; padding-bottom:10px;">
                  ${escapeHtml(companyName)}
                </td>
              </tr>
              <tr>
                <td style="padding-bottom:10px;">
                  <table cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td style="width:100%; height:2px; background-color:${BRAND_RED}; font-size:1px; line-height:1px;">&nbsp;</td>
                    </tr>
                  </table>
                </td>
              </tr>
              ${detailRows}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
`.trim();
}
function buildSocialLink(url, iconSrc, alt, iconName) {
    const { width, height } = getModernSignatureIconSize(iconName);
    return `
    <a href="${escapeAttr(url)}" aria-label="${escapeHtml(alt)}" style="text-decoration:none; display:inline-block; margin:0 4px;">
      <img src="${escapeAttr(iconSrc)}" alt="${escapeHtml(alt)}" width="${width}" height="${height}" border="0" style="display:block; width:${width}px; height:${height}px;" />
    </a>`;
}
function buildDetailRow(iconSrc, content, iconName, iconTop = false) {
    const valign = iconTop ? "top" : "middle";
    const iconPadding = iconTop
        ? "padding-right:8px; padding-top:1px;"
        : "padding-right:8px;";
    const textStyle = iconTop
        ? `font-size:13px; color:${TEXT_BODY}; line-height:1.5; font-family:${FONT_STACK};`
        : `font-size:13px; color:${TEXT_BODY}; font-family:${FONT_STACK}; line-height:16px;`;
    const { width, height } = getModernSignatureIconSize(iconName);
    return `
  <tr>
    <td style="padding-bottom:6px; font-size:13px; color:${TEXT_BODY}; font-family:${FONT_STACK}; line-height:16px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td valign="${valign}" style="${iconPadding}">
            <img src="${escapeAttr(iconSrc)}" alt="" width="${width}" height="${height}" border="0" style="display:block; width:${width}px; height:${height}px;" />
          </td>
          <td valign="${valign}" style="${textStyle}">
            ${content}
          </td>
        </tr>
      </table>
    </td>
  </tr>`;
}
function formatLocation(profile, options = {}) {
    const parts = [
        profile.city,
        options.omitState ? "" : profile.state,
        profile.country,
    ].filter(Boolean);
    if (parts.length > 0) {
        return parts.join(", ");
    }
    if (profile.officeLocation) {
        return profile.officeLocation;
    }
    if (!options.omitStreetAddress && profile.streetAddress) {
        return profile.streetAddress;
    }
    return DEFAULT_LOCATION;
}
function formatWebsiteLabel(url) {
    return url.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}
function resolveSocialProfile(profile, prefs) {
    const explicitTwitterHandle = normalizeTwitterHandle(prefs.twitterHandle);
    const fallbackTwitterHandle = normalizeTwitterHandle(profile.streetAddress);
    const twitterHandle = explicitTwitterHandle || fallbackTwitterHandle;
    const explicitLinkedInUrl = normalizeLinkedInUrl(prefs.linkedInUrl);
    const fallbackLinkedInUrl = normalizeLinkedInUrl(profile.state);
    const linkedInUrl = explicitLinkedInUrl || fallbackLinkedInUrl;
    return {
        twitterHandle,
        linkedInUrl,
        usingStreetFallback: !explicitTwitterHandle && Boolean(fallbackTwitterHandle),
        usingStateFallback: !explicitLinkedInUrl && Boolean(fallbackLinkedInUrl),
    };
}
function normalizeTwitterHandle(value) {
    const trimmed = (value !== null && value !== void 0 ? value : "").trim();
    if (!trimmed) {
        return "";
    }
    const withoutProtocol = trimmed
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "");
    const urlMatch = withoutProtocol.match(/^(?:x\.com|twitter\.com)\/@?([^/?#]+)/i);
    if (urlMatch === null || urlMatch === void 0 ? void 0 : urlMatch[1]) {
        return urlMatch[1].trim().replace(/^@+/, "");
    }
    return trimmed.replace(/^@+/, "").replace(/[/?#].*$/, "").trim();
}
function normalizeLinkedInUrl(value) {
    const trimmed = (value !== null && value !== void 0 ? value : "").trim();
    if (!trimmed) {
        return "";
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    const withoutProtocol = trimmed
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "");
    if (/^linkedin\.com\//i.test(withoutProtocol)) {
        return `https://${withoutProtocol}`;
    }
    const normalizedPath = trimmed.replace(/^\/+|\/+$/g, "");
    if (/^(?:in|company|school)\//i.test(normalizedPath)) {
        return `https://www.linkedin.com/${normalizedPath}`;
    }
    return `https://www.linkedin.com/in/${normalizedPath}`;
}
function mergeWithOverrides(profile, prefs) {
    return Object.assign(Object.assign({}, profile), prefs.overrides);
}
function escapeHtml(str) {
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
function escapeAttr(str) {
    const clean = str.replace(/^javascript:/i, "");
    return encodeURI(clean).replace(/"/g, "%22");
}
